/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import { logger } from '../utils/logger';
import { LocalIndexedDBStoreBackend } from './indexeddb-local-backend';

type ICmd = {
  command: string;
  seq: number;
  args: any[];
};

/**
 * This class lives in the webworker and drives a LocalIndexedDBStoreBackend
 * controlled by messages from the main process.
 *
 * @example
 * It should be instantiated by a web worker script provided by the application
 * in a script, for example:
 * ```
 * import {IndexedDBStoreWorker} from 'matrix-js-sdk/lib/indexeddb-worker.js';
 * const remoteWorker = new IndexedDBStoreWorker(postMessage);
 * onmessage = remoteWorker.onMessage;
 * ```
 *
 * Note that it is advisable to import this class by referencing the file directly to
 * avoid a dependency on the whole js-sdk.
 *
 */
export class IndexedDBStoreWorker {
  private backend?: LocalIndexedDBStoreBackend;

  /**
   * @param postMessage - The web worker postMessage function that
   * should be used to communicate back to the main script.
   */
  public constructor(private readonly postMessage: InstanceType<typeof Worker>['postMessage']) {}

  private onClose = (): void => {
    this.postMessage.call(null, {
      command: 'closed',
    });
  };

  /**
   * Passes a message event from the main script into the class. This method
   * can be directly assigned to the web worker `onmessage` variable.
   *
   * @param ev - The message event
   */
  public onMessage = (ev: MessageEvent): void => {
    const msg: ICmd = ev.data;
    let prom: Promise<any> | undefined;

    switch (msg.command) {
      case 'setupWorker':
        // this is the 'indexedDB' global (where global != window
        // because it's a web worker and there is no window).
        // msg.args[0] = dbName, msg.args[1] = userId
        this.backend = new LocalIndexedDBStoreBackend(indexedDB, msg.args[0], msg.args[1]);
        prom = Promise.resolve();
        break;
      case 'connect':
        prom = this.backend?.connect(this.onClose);
        break;
      case 'isNewlyCreated':
        prom = this.backend?.isNewlyCreated();
        break;
      case 'clearDatabase':
        prom = this.backend?.clearDatabase();
        break;
      case 'syncToDatabase':
        prom = this.backend?.syncToDatabase(msg.args[0]);
        break;
      case 'getUserPresenceEvents':
        prom = this.backend?.getUserPresenceEvents();
        break;
      case 'getNextBatchToken':
        break;
      case 'getClientOptions':
        prom = this.backend?.getClientOptions();
        break;
      case 'storeClientOptions':
        prom = this.backend?.storeClientOptions(msg.args[0]);
        break;
      case 'getSavedRooms':
        prom = this.backend?.getSavedRooms(msg.args[0]);
        break;
      case 'setRoomsData':
        prom = this.backend?.setRoomsData(msg.args[0]);
        break;
      case 'saveToDeviceBatches':
        break;
    }

    if (prom === undefined) {
      this.postMessage({
        command: 'cmd_fail',
        seq: msg.seq,
        // Can't be an Error because they're not structured cloneable
        error: 'Un recognized command',
      });
      return;
    }

    prom.then(
      (ret) => {
        this.postMessage.call(null, {
          command: 'cmd_success',
          seq: msg.seq,
          result: ret,
        });
      },
      (err) => {
        logger.error(`Error running command: ${msg.command}`, err);
        this.postMessage.call(null, {
          command: 'cmd_fail',
          seq: msg.seq,
          // Just send a string because Error objects aren't cloneable
          error: {
            message: err.message,
            name: err.name,
          },
        });
      },
    );
  };
}
